/* eslint-disable camelcase */
import { VerticalProgressBarWithElements } from '@components';
import { useNavigateWithOfflineMode } from '@hooks';
import { Flex, theme, Typography } from '@styles';
import { ReactComponent as Check } from '@svgs/check.svg';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  shape,
  string,
} from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Icon } from '../Icon';

const LearningMapCard = ({
  containerRef,
  courseId,
  isFirst,
  isLast,
  module,
  onClick,
  openSkipModal,
  padding,
}) => {
  const ref = useRef(null);
  const { navigate } = useNavigateWithOfflineMode();
  const { id, image, is_completed, is_highlighted_on_map, lessons, title } =
    module;

  const [scrollLeftPosition, setScrollLeftPosition] = useState(0);

  useEffect(() => {
    if (containerRef?.current) {
      // eslint-disable-next-line no-param-reassign
      containerRef.current.scrollLeft += scrollLeftPosition;
    }
  });

  useEffect(() => {
    if (is_highlighted_on_map && !is_completed) {
      if (!ref?.current) {
        return;
      }

      const scrollRect = containerRef?.current?.getBoundingClientRect();
      const activeRect = ref?.current?.getBoundingClientRect();

      setScrollLeftPosition(
        activeRect.left -
          scrollRect.left -
          scrollRect.width / 2 +
          activeRect.width / 2
      );
    }
  }, [is_highlighted_on_map, is_completed, id, ref, containerRef]);

  const handleClickLesson = (lessonId) => () => {
    navigate(`/courses/${courseId}/${id}`, {
      state: { lessonId },
    });
  };

  return (
    <Flex
      ref={ref}
      grow={0}
      pl={isFirst ? padding : 0}
      pr={isLast ? padding : 0}
    >
      <Flex flexDirection="column" width={300}>
        <Flex
          alignItems="center"
          backdropFilter="blur(17px)"
          bg={
            is_highlighted_on_map && !is_completed
              ? theme.colors.schoolBusYellow[20]
              : theme.colors.white[20]
          }
          borderColor={theme.colors.white[10]}
          borderStyle="solid"
          borderWidth={1}
          gap={16}
          height={96}
          p={16}
          position="relative"
        >
          {is_completed && (
            <Icon height={64} position="absolute" SVG={Check} width={64} />
          )}
          <Flex
            backgroundImage={image}
            backgroundSize="cover"
            flexShrink={0}
            height={64}
            width={64}
          />
          <Typography
            color={theme.colors.white[100]}
            variant="MS-regular-s16-lh25"
          >
            {title}
          </Typography>
        </Flex>
        {lessons?.length > 0 && (
          <Flex
            backdropFilter="blur(17px)"
            bg={theme.colors.white[10]}
            borderColor={theme.colors.white[10]}
            borderStyle="solid"
            borderWidth={1}
            p={16}
          >
            <VerticalProgressBarWithElements
              elements={lessons}
              handleClick={onClick || handleClickLesson}
              handleFinalElementClick={openSkipModal?.({
                courseId,
                id,
                isCompleted: is_completed,
              })}
              isCurrent={is_highlighted_on_map && !is_completed}
            />
          </Flex>
        )}
      </Flex>
      {!isLast && (
        <Flex bg={theme.colors.white[100]} height={1} mt={48} width={48} />
      )}
    </Flex>
  );
};

LearningMapCard.propTypes = {
  containerRef: node.isRequired,
  courseId: number.isRequired,
  isFirst: bool,
  isLast: bool,
  module: shape({
    image: string.isRequired,
    is_completed: bool.isRequired,
    is_highlighted_on_map: bool.isRequired,
    lessons: arrayOf(object),
    title: string.isRequired,
  }).isRequired,
  onClick: func,
  openSkipModal: func.isRequired,
  padding: number.isRequired,
};

LearningMapCard.defaultProps = {
  isFirst: false,
  isLast: false,
  onClick: null,
};

export default LearningMapCard;
